import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useMutation, gql } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// Define the login mutation
const LOGIN_MUTATION = gql`
  mutation Login($eventId: String!, $email: String!, $password: String!) {
    login(eventId: $eventId, email: $email, password: $password)
  }
`;

const LoginPage = () => {
  const [eventId, setEventId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { setIsAuthenticated } = useAuth();

  const [login, { loading }] = useMutation(LOGIN_MUTATION, {
    onCompleted: (data) => {
      if (data?.login) {
        // Store the token first
        localStorage.setItem("auth.leadgen.eventegration.com", data.login);
        // Update authentication state
        setIsAuthenticated(true);
        // Force a localStorage event to update other tabs
        window.dispatchEvent(new Event("storage"));
        // Then navigate
        navigate("/dashboard");
      } else {
        setError("Login failed. Please try again.");
      }
    },
    onError: (error) => {
      console.error("Login error:", error);
      setError(error.message || "Login failed. Please check your credentials.");
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!eventId || !email || !password) {
      setError("All fields are required");
      return;
    }

    try {
      await login({ variables: { eventId, email, password } });
    } catch (err) {
      // Error is already handled in onError callback
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
        p: 3,
      }}
    >
      <Card sx={{ maxWidth: 400, width: "100%", mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
            }}
          >
            {/*
              * NO EVENT LOGO HERE.
              *
              * This was one event's logo (5087644490), hardcoded — and the login page is the
              * one screen that cannot know which event you belong to, because you have not
              * signed in yet. So every exhibitor at every event was shown another company's
              * branding before they could type their password.
              *
              * The dashboard already does this properly: once the token names the event, the
              * layout picks that event's logo and theme. Nothing is lost by leaving this page
              * neutral, and nothing put here could be right for everyone.
              */}
            <Typography variant="h4" component="h1" align="center" gutterBottom>
              Login
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Sign in to access your dashboard
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label="Event ID"
              variant="outlined"
              fullWidth
              margin="normal"
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              required
            />

            <TextField
              label="Email"
              type="text"
              variant="outlined"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <TextField
              label="Password"
              type="password"
              variant="outlined"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 3 }}
            >
              {loading ? <CircularProgress size={24} /> : "Login"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* For debugging purposes */}
      <Button
        variant="outlined"
        color="secondary"
        onClick={() => window.open("https://eventegration.com", "_blank")}
        size="small"
      >
        Powered by Eventegration
      </Button>
    </Box>
  );
};

export default LoginPage;
